import i18n from "@/i18n/config";
import { ERROR_STATUS_CODES } from "@/constants/error";
import { AppRoutes } from "@/routes/config";
import { TApiError } from "@/types/api";
import { getLogoutUrl, logout } from "@/utils/api";
import { toast } from "@/utils/toast";
import { AxiosError } from "axios";
import Cookies from "js-cookie";
import { FieldErrors, FieldValues, Path, UseFormReturn } from "react-hook-form";
import { ValidationError } from "yup";

/**
 * Checks if the error is an API error.
 *
 * @param {unknown} error - The error to check.
 * @returns {error is TApiError} - True if the error is an API error, false otherwise.
 */
export const isApiError = (error: unknown): error is TApiError => {
  if (error && typeof error === "object" && "message" in error) return true;
  return false;
};

// Axios error codes that mean the request never got a server response (transport-level failure).
// NB: `ERR_CANCELED` (request aborted in-flight, e.g. by a page reload/navigation) is included
// INTENTIONALLY — an aborted request never proves the session is valid or invalid, so we keep the
// session and retry rather than log out. Do not "fix" this by removing it.
const NETWORK_ERROR_CODES = ["ERR_NETWORK", "ECONNABORTED", "ETIMEDOUT", "ERR_CANCELED"];

/**
 * Checks whether an error is a *transport-level* failure — i.e. the request never received
 * an HTTP response from the server.
 *
 * Axios reports these as:
 *  - `ERR_NETWORK`   — connection failed / DNS / TLS / offline (no response at all)
 *  - `ECONNABORTED` / `ETIMEDOUT` — request timed out before any response
 *  - `ERR_CANCELED`  — request aborted in-flight (e.g. a page reload/navigation tearing it down)
 * plus the general case of an AxiosError that has a request but **no** `response`.
 *
 * @param {unknown} error - The error to check.
 * @returns {boolean} - True when the failure is network/transport level (no server response).
 */
export const isNetworkError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  const err = error as AxiosError;
  if (err.code && NETWORK_ERROR_CODES.includes(err.code)) return true;
  // An AxiosError that reached the request stage but got no response = nothing came back from the server.
  if (err.isAxiosError && !err.response) return true;
  return false;
};

/**
 * Checks if the error is a validation error.
 *
 * @param {unknown} error - The error to check.
 * @returns {error is ValidationError} - True if the error is a validation error, false otherwise.
 */
export const isValidatorError = (error: unknown): error is ValidationError => {
  if (error && typeof error === "object" && "path" in error && "message" in error) return true;
  return false;
};

/**
 * Sets form error messages based on the error type.
 *
 * @template T - The type of form field values.
 * @param {UseFormReturn<T, object, undefined>} methods - The form methods from react-hook-form.
 * @param {unknown} error - The error to handle.
 */
export const setErrorForm = <T extends FieldValues>(methods: UseFormReturn<T, object, T>, error: unknown) => {
  const { setError } = methods;
  if (isValidatorError(error)) {
    setError(error.path as Path<T>, { message: error.message });
    return;
  }
  if (isApiError(error)) {
    if (error.error && Array.isArray(error.error)) {
      error.error.forEach(({ property, message }) => {
        setError(property as Path<T>, { message });
      });
      return;
    }
  }
};

/**
 * Displays an error message using the toast utility.
 *
 * @param {unknown} error - The error to handle.
 */
export const showError = (error: unknown) => {
  // Already toasted by the axios interceptor
  if (isApiError(error) && error.code === ERROR_STATUS_CODES.WEB_ACCESS_NOT_ALLOWED) return;

  if (isApiError(error) && (error.message || error.error)) {
    // Use i18n translation for known error codes
    if (error.code) {
      const i18nKey = `common.apiErrors.${error.code}`;
      if (i18n.exists(i18nKey)) {
        toast.error(i18n.t(i18nKey));
        return;
      }
    }
    if (error.error) {
      if (Array.isArray(error.error)) {
        toast.error(error.error.map((e) => e.message).join("\n"));
      } else if (typeof error.error === "string") {
        toast.error(error.error);
      } else {
        toast.error(error.message);
      }
    } else if (error.message) {
      toast.error(error.message);
    }
  } else {
    toast.error(i18n.t("common.unknownError"));
  }
};

/**
 * Handles API errors based on error code and redirects if necessary.
 *
 * @param {AxiosError} error - The error to handle.
 */
export const handleApiErrorWithCode = (error: AxiosError) => {
  if (error.code === "ERR_NETWORK") {
    setTimeout(() => window.location.replace(AppRoutes.serverErrorPage), 1000);
    return;
  }

  // Handle maintenance errors for all HTTP methods (skip if already on target page)
  if (isApiError(error.response?.data)) {
    const { code } = error.response.data;
    if (code === ERROR_STATUS_CODES.MAINTENANCE_UNUSABLE) {
      if (!window.location.pathname.startsWith(AppRoutes.maintenance)) {
        window.location.replace(AppRoutes.maintenance);
      }
      return;
    }
    if (code === ERROR_STATUS_CODES.MAINTENANCE_READABLE) {
      if (window.location.pathname !== AppRoutes.home) {
        window.location.replace(AppRoutes.home);
      }
      return;
    }
  }

  if (error.config?.method === "get") {
    if (isApiError(error.response?.data)) {
      const { code, message } = error.response.data;
      const excludedCodes = [ERROR_STATUS_CODES.ZIP_CODE_ERROR, ERROR_STATUS_CODES.ZIP_CODE_INVALID, ERROR_STATUS_CODES.ASSESSMENT_ALREADY_COMPLETED];
      if (!excludedCodes.includes(code)) {
        /* Prefer i18n translation for known error codes over raw BE message */
        const i18nKey = code ? `common.apiErrors.${code}` : "";
        toast.error(i18nKey && i18n.exists(i18nKey) ? i18n.t(i18nKey) : message);
      }
    }
  }

  if (error.response?.status === 403 && isApiError(error.response?.data)) {
    const { code } = error.response.data;
    switch (code) {
      case ERROR_STATUS_CODES.RSO_ACCOUNT_REGISTRATION_CANCELED:
      case ERROR_STATUS_CODES.RSO_USER_NOT_ACTIVE: {
        if (error.config?.url !== AppRoutes.login) {
          window.location.replace(getLogoutUrl());
        } else {
          logout();
        }
        return;
      }
    }
  }

  if (error.response?.status === 404 && error.config?.method === "get") {
    const errorUrl = error.config?.url || "";
    const isDetailApiCall =
      /\/auth\/company\/\d+/.test(errorUrl) ||
      /\/company\/\d+/.test(errorUrl) ||
      /\/talents\/\d+/.test(errorUrl) ||
      /\/jobs\/\d+/.test(errorUrl) ||
      /\/cases\/\d+/.test(errorUrl);

    const currentPath = window.location.pathname;
    const isDetailOrEditPage = currentPath.includes("/detail") || currentPath.includes("/edit");

    if (!isDetailApiCall && !isDetailOrEditPage && !currentPath.startsWith(AppRoutes.pageNotFound)) {
      window.location.replace(AppRoutes.pageNotFound);
    }
  }

  if (error.response?.status === 500 && error.config?.method === "get") {
    const errorUrl = error.config?.url || "";
    const isDetailApiCall =
      /\/auth\/company\/\d+/.test(errorUrl) ||
      /\/company\/\d+/.test(errorUrl) ||
      /\/talents\/\d+/.test(errorUrl) ||
      /\/jobs\/\d+/.test(errorUrl) ||
      /\/cases\/\d+/.test(errorUrl);

    const currentPath = window.location.pathname;
    const isDetailOrEditPage = currentPath.includes("/detail") || currentPath.includes("/edit");

    if (!isDetailApiCall && !isDetailOrEditPage && !currentPath.startsWith(AppRoutes.serverErrorPage)) {
      setTimeout(() => window.location.replace(AppRoutes.serverErrorPage), 1000);
    }
  }
};

export const getCustomSentryExceptionError = (error: AxiosError) => {
  const titleSentry = "Irohana Webview Error";
  const serverErrorResponse = error.response?.data as { message: string; code: string; error: unknown };
  let name = `[Irohana Webview]`;
  if (serverErrorResponse) {
    const customErrorResponseMessage = serverErrorResponse?.error
      ? typeof serverErrorResponse?.error === "string"
        ? serverErrorResponse?.error
        : JSON.stringify(serverErrorResponse?.error)
      : undefined;
    name = name + `: ${serverErrorResponse.message}`;
    const message = `${serverErrorResponse.code}\n${customErrorResponseMessage ?? ""}`;
    const user = { accountId: Cookies.get("learner-account-id"), userId: Cookies.get("learner-user-id") };
    const sendError = new Error(`${name} \n ${message} \n ${JSON.stringify(user)}`);
    sendError.name = titleSentry;
    return sendError;
  } else {
    const sendError = new Error(`${name} \n ${error.message}`);
    sendError.name = titleSentry;
    return sendError;
  }
};

export function findIntegerErrorPaths(errors: FieldErrors<FieldValues>, pathPrefix = ""): string[] {
  const paths: string[] = [];

  for (const key in errors) {
    const value = errors[key];

    const currentPath = pathPrefix ? `${pathPrefix}.${key}` : key;

    if (
      typeof value === "object" &&
      value !== null &&
      "type" in value &&
      typeof value.type === "string" &&
      ["integer", "typeError", "type"].includes(value.type)
    ) {
      paths.push(currentPath);
    } else if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (typeof item === "object" && item !== null) {
          const nestedPaths = findIntegerErrorPaths(item as FieldErrors<FieldValues>, `${currentPath}[${index}]`);
          paths.push(...nestedPaths);
        }
      });
    } else if (typeof value === "object" && value !== null) {
      const nestedPaths = findIntegerErrorPaths(value as FieldErrors<FieldValues>, currentPath);
      paths.push(...nestedPaths);
    }
  }

  return paths;
}

export const triggerIntegerSaveDraftError = async <T extends FieldValues>(methods: UseFormReturn<T, object, undefined>): Promise<boolean> => {
  const errorPaths = findIntegerErrorPaths(methods.formState.errors);

  if (errorPaths.length > 0) {
    const firstPath = errorPaths[0];
    const normalizedPath = firstPath?.replace(/\[(\d+)\]/g, ".$1");

    setTimeout(() => {
      const el = document.querySelector(`[name="${normalizedPath}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);

    return true;
  }

  return false;
};
